import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    InputNumber
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import moment from 'moment';
const { TextArea } = Input;
const { Option } = Select;


interface SubscriptionDaa {
    subscription_id: number;
    organization_name: string;
    subscription_organization_id: string;
    subscription_status: number;
    package_name: string;
    subscription_package_id: number;
    subscription_expiry_date: string;
    subscription_expire_status: string;
    subcription_amount: number;
    subcription_discount: number;
    subcription_after_discount_amount: number;
}
interface Package {
    package_id: number;
    package_name: string;
    package_amount: string;
    package_duration: string;
}

interface Organization {
    organization_id: number;
    organization_name: string;
}

interface SubscriptionModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        subscription_id?: number;
        organization_name?: string;
        subscription_organization_id?: string;
        subscription_package_id?: number;
        subscription_expiry_date?: string; // ISO date string
        subscription_expire_status?: string;
        subcription_amount?: number;
        subcription_discount?: number;
        subcription_after_discount_amount?: number;
    } | null;
    isEditMode?: boolean;
    subscriptions: SubscriptionDaa[];
}

const SubscriptionModel: React.FC<SubscriptionModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [packages, setPackage] = useState<Package[]>([]);;
    const [organizations, setOrganization] = useState<Organization[]>([]);;

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddSubscription = async (values: any) => {
        try {
            const dataVal = {
                subscription_package_id: values.subscription_package_id,
                subscription_organization_id: values.subscription_organization_id,
                subscription_expiry_date: values.subscription_expiry_date ? moment(values.subscription_expiry_date).format('YYYY-MM-DD')  : null,
                subscription_expire_status: values.subscription_expire_status,
                subcription_amount: values.subcription_amount,
                subcription_discount: values.subcription_discount,
                subcription_after_discount_amount: values.subcription_after_discount_amount,
                ...(initialValues?.subscription_id && { subscription_id: initialValues.subscription_id }),
            };
            const url = isEditMode
                ? 'edit-subscription'
                : 'add-subscription';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Subscription updated successfully!' : 'Subscription created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} subscription`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                subscription_organization_id: initialValues.subscription_organization_id,
                organization_name: initialValues.organization_name,
                subscription_package_id: initialValues.subscription_package_id ?? 0,
                subscription_expiry_date: initialValues.subscription_expiry_date ? moment(initialValues.subscription_expiry_date) : null,
                subscription_expire_status: initialValues.subscription_expire_status ?? undefined,
                subcription_amount: initialValues.subcription_amount ?? undefined,
                subcription_discount: initialValues.subcription_discount ?? undefined,
                subcription_after_discount_amount: initialValues.subcription_after_discount_amount ?? undefined,
            });
        } else if (visible) {
            form.resetFields();
        }
        console.log("initialValues", initialValues)
    }, [visible, isEditMode, initialValues, form]);

    const amount = Form.useWatch('subcription_amount', form);
    const discount = Form.useWatch('subcription_discount', form);

    useEffect(() => {
        const afterDiscount = Math.max(0, (amount || 0) - (discount || 0));
        form.setFieldsValue({
            subcription_after_discount_amount: afterDiscount,
        });
    }, [amount, discount]);
    const fetchPackage = async () => {
        try {
            const data = await apiRequest<Package[]>({
                method: 'get',
                url: 'package',
            });
            setPackage(data);
        } catch (error) {
            showError('Failed to fetch Package');
            console.error(error);
        }
    };
    const fetchOrganization = async () => {
        try {
            const data = await apiRequest<Organization[]>({
                method: 'get',
                url: 'organization',
            });
            setOrganization(data);
        } catch (error) {
            showError('Failed to fetch Package');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchPackage();
        fetchOrganization();
    }, []);

    return (
        <Modal
            title={isEditMode ? 'Edit Subscription' : 'Add New Subscription'}
            open={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddSubscription}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="subscription_package_id"
                            className="px-8"
                            label="Package Name"
                            rules={[{ required: true, message: 'Please select a package name' }]}
                        >
                            <Select
                                placeholder="Select Package Name"
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={packages.map(p => ({
                                    value: p.package_id,
                                    label: p.package_name,
                                }))}
                                onChange={(value) => {
                                    const selectedPackage = packages.find(p => p.package_id === value);
                                    if (selectedPackage) {
                                        const months = parseInt(selectedPackage.package_duration || '0', 10);
                                        const expiryDate = new Date();
                                        expiryDate.setMonth(expiryDate.getMonth() + months);

                                        form.setFieldsValue({
                                            subcription_amount: Number(selectedPackage.package_amount),
                                            subcription_after_discount_amount: Number(selectedPackage.package_amount),
                                            subcription_discount: 0,
                                            subscription_expiry_date: moment(expiryDate),
                                        });
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="subscription_organization_id"
                            className="px-8"
                            label="Organization Name"
                            rules={[{ required: true, message: 'Please select a organization name' }]}
                        >
                            <Select
                                disabled={isEditMode ? true : false}
                                placeholder="Select Organization Name"
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    { value: 0, label: 'Select Organization Name' },
                                    ...organizations.map((organization) => ({
                                        value: organization.organization_id,
                                        label: organization.organization_name,
                                    })),
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    {/* Existing fields... */}
                    <Col span={12}>
                        <Form.Item
                            name="subscription_expiry_date"
                            label="Subscription Expiry Date"
                            rules={[{ required: true, message: 'Please select expiry date' }]}
                        >
                            <DatePicker
                                className="py-7 border-radious" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="subscription_expire_status"
                            label="Subscription Expire Status"
                            rules={[{ required: true, message: 'Please select expire status' }]}
                        >
                            <Select placeholder="Select Expire Status">
                                <Select.Option value="active">Active</Select.Option>
                                <Select.Option value="expired">Expired</Select.Option>
                                <Select.Option value="pending">Pending</Select.Option>
                                {/* Add other statuses if needed */}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="subcription_amount"
                            label="Subscription Amount"
                            rules={[{ required: true, message: 'Please input subscription amount' }]}
                        >
                            <InputNumber
                                className="py-7 border-radious"
                                style={{ width: '100%' }}
                                min={0}
                                formatter={value => `₹  ${value}`}
                                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                    const nativeEvent = e.nativeEvent as InputEvent;
                                    const inputChar = nativeEvent.data;
                                    const currentValue = (e.target as HTMLInputElement).value;

                                    if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 12) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="subcription_discount"
                            label="Subscription Discount"
                            rules={[{ required: true, message: 'Please input subscription discount' }]}
                        >
                            <InputNumber
                                className="py-7 border-radious"
                                style={{ width: '100%' }}
                                min={0}
                                formatter={value => `₹ ${value}`}
                                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                    const nativeEvent = e.nativeEvent as InputEvent;
                                    const inputChar = nativeEvent.data;
                                    const currentValue = (e.target as HTMLInputElement).value;

                                    if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 12) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="subcription_after_discount_amount"
                            label="Amount After Discount"
                            rules={[{ required: true, message: 'Please input amount after discount' }]}
                        >
                            <InputNumber
                                disabled
                                className="py-7 border-radious"
                                style={{ width: '100%' }}
                                min={0}
                                formatter={value => `₹ ${value}`}
                                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                    const nativeEvent = e.nativeEvent as InputEvent;
                                    const inputChar = nativeEvent.data;
                                    const currentValue = (e.target as HTMLInputElement).value;

                                    if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 12) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Subscription
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SubscriptionModel;
